import React from 'react'
import GlobalStyles from '../../../../components/component-items/styles'
import CardLoading from '../../../../components/component-items/loading-popover'
import { Pages } from '../../../../components/component-items/pager'
import { TableCell } from '../../../../components/component-items/datatable'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import { OrderListFiltered } from '../batch/order-list'
import { friendlyValue } from './wave-utils'

export type DirectiveReservationPreviewMetrics = {
  selected: number | null
  reservable: number | null
  not_reservable: number | null
  units_reserved: number | null
  product_variants: number | null
}

export type DirectiveReservationPreviewInventoryRow = {
  order_id: number
  order_number: string
  sku: string
  quantity: number
  inventory_type: string
  location: string
  zone?: string | number | null
  aisle?: string | number | null
  bay?: string | number | null
  level?: string | number | null
  slot?: string | number | null
  lot_number?: string | null
  inventory_control_label?: string | null
  inventory_control_value?: string | null
  reservation_step?: {
    id?: string
    name?: string
  }
}

const previewValue = (value?: number | null) => (value == null ? '-' : value)

const formatLocationParts = (item: DirectiveReservationPreviewInventoryRow) => {
  const parts = [
    item.zone ? `Zone ${item.zone}` : '',
    item.aisle ? `Aisle ${item.aisle}` : '',
    item.bay ? `Bay ${item.bay}` : '',
    item.level ? `Level ${item.level}` : '',
    item.slot ? `Slot ${item.slot}` : '',
  ].filter(Boolean)
  return parts.join(' · ')
}

const inventoryControlValue = (item: DirectiveReservationPreviewInventoryRow) =>
  item.inventory_control_value || item.lot_number || 'NA'

const inventoryControlLabel = (item: DirectiveReservationPreviewInventoryRow) =>
  item.inventory_control_label || (item.lot_number ? 'Lot Number' : '')

type PreviewSectionProps = {
  title: string
  count?: number | string | null
  open: boolean
  setOpen: (open: boolean) => void
  children: React.ReactNode
}

export const DirectivePreviewSection = ({ title, count, open, setOpen, children }: PreviewSectionProps) => (
  <div style={{ margin: '0.7em 0.9em 1em', border: '1px solid #ebedf2', borderRadius: 6, overflow: 'hidden' }}>
    <button
      type="button"
      onClick={() => setOpen(!open)}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        border: 0,
        background: '#fbfcff',
        padding: '0.75em 1em',
        color: '#495057',
        fontWeight: 700,
        textAlign: 'left',
      }}
    >
      <span>
        {title} {count != null ? <small style={{ color: '#74788d' }}>({count})</small> : null}
      </span>
      <span style={{ color: '#5d78ff' }}>{open ? 'Collapse' : 'Expand'}</span>
    </button>
    {open ? <div style={{ padding: '1em' }}>{children}</div> : null}
  </div>
)

export const DirectivePreviewSummaryCards = ({ metrics }: { metrics: DirectiveReservationPreviewMetrics | null }) => (
  <div style={{ margin: '1em' }}>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1em' }}>
      <div style={{ flex: '2 1 420px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1em' }}>
          <StatusBoxStyled className="silver" style={{ padding: '1em', textAlign: 'left' }}>
            <p style={{ margin: 0 }}>Selected Orders</p>
            <h4 style={{ margin: '0.25em 0 0' }}>{previewValue(metrics?.selected)}</h4>
          </StatusBoxStyled>
          <StatusBoxStyled className="green" style={{ padding: '1em', textAlign: 'left' }}>
            <p style={{ margin: 0 }}>Reservable Orders</p>
            <h4 style={{ margin: '0.25em 0 0' }}>{previewValue(metrics?.reservable)}</h4>
          </StatusBoxStyled>
          <StatusBoxStyled
            className={metrics?.not_reservable ? 'yellow' : 'silver'}
            style={{ padding: '1em', textAlign: 'left' }}
          >
            <p style={{ margin: 0 }}>Not Reservable Orders</p>
            <h4 style={{ margin: '0.25em 0 0' }}>{previewValue(metrics?.not_reservable)}</h4>
          </StatusBoxStyled>
        </div>
      </div>
      <div style={{ flex: '1 1 280px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1em' }}>
          <StatusBoxStyled className="blue" style={{ padding: '1em', textAlign: 'left' }}>
            <p style={{ margin: 0 }}>Units Reserved</p>
            <h4 style={{ margin: '0.25em 0 0' }}>{previewValue(metrics?.units_reserved)}</h4>
          </StatusBoxStyled>
          <StatusBoxStyled className="silver" style={{ padding: '1em', textAlign: 'left' }}>
            <p style={{ margin: 0 }}>Variants</p>
            <h4 style={{ margin: '0.25em 0 0' }}>{previewValue(metrics?.product_variants)}</h4>
          </StatusBoxStyled>
        </div>
      </div>
    </div>
    <div style={{ color: '#74788d', fontSize: '0.9em', marginTop: '0.75em' }}>
      Preview only. No inventory is committed until the wave is run.
    </div>
  </div>
)

export const DirectivePreviewInventoryTable = ({
  rows,
  emptyText = 'No inventory rows selected.',
}: {
  rows: DirectiveReservationPreviewInventoryRow[]
  emptyText?: string
}) => (
  <GlobalStyles.DataTableContainer>
    <GlobalStyles.DataTable>
      <thead>
        <tr>
          <th>
            <span className="text center">Order</span>
          </th>
          <th>
            <span className="text center">SKU</span>
          </th>
          <th>
            <span className="text center">Quantity</span>
          </th>
          <th>
            <span className="text center">Inventory Type</span>
          </th>
          <th>
            <span className="text center">Location</span>
          </th>
          <th>
            <span className="text center">Inventory Control</span>
          </th>
          <th>
            <span className="text center">Step</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.length ? (
          rows.map((item, index) => (
            <GlobalStyles.TableRow key={`${item.order_id}-${item.sku}-${item.location}-${index}`}>
              <TableCell center>{item.order_number}</TableCell>
              <TableCell center>{item.sku}</TableCell>
              <TableCell center>{item.quantity}</TableCell>
              <TableCell center>{friendlyValue(item.inventory_type)}</TableCell>
              <TableCell center>
                <div>{item.location || 'NA'}</div>
                <div style={{ color: '#74788d', fontSize: '0.9em' }}>{formatLocationParts(item)}</div>
              </TableCell>
              <TableCell center>
                <div>{inventoryControlValue(item)}</div>
                {inventoryControlLabel(item) ? (
                  <div style={{ color: '#74788d', fontSize: '0.9em' }}>{inventoryControlLabel(item)}</div>
                ) : null}
              </TableCell>
              <TableCell center>{item.reservation_step?.name || 'Primary reservation step'}</TableCell>
            </GlobalStyles.TableRow>
          ))
        ) : (
          <GlobalStyles.TableRow>
            <TableCell center colspan={7}>
              {emptyText}
            </TableCell>
          </GlobalStyles.TableRow>
        )}
      </tbody>
    </GlobalStyles.DataTable>
  </GlobalStyles.DataTableContainer>
)

export const DirectivePreviewOrderSection = ({
  orders,
  count,
  open,
  setOpen,
  page,
  setPage,
  pageSize = 10,
  loading = false,
  loaded = true,
}: {
  orders: any[]
  count: number
  open: boolean
  setOpen: (open: boolean) => void
  page: number
  setPage: (page: number) => void
  pageSize?: number
  loading?: boolean
  loaded?: boolean
}) => (
  <DirectivePreviewSection title="Order Results" count={loaded ? count : '-'} open={open} setOpen={setOpen}>
    <GlobalStyles.DataTableContainer>
      {loaded ? (
        orders.length ? (
          <OrderListFiltered orders={orders} />
        ) : (
          <div style={{ padding: '1em', textAlign: 'center', color: '#74788d' }}>No orders matched this directive.</div>
        )
      ) : (
        <GlobalStyles.DataTable>
          <tbody>
            <GlobalStyles.TableRow>
              <TableCell center>&nbsp;</TableCell>
            </GlobalStyles.TableRow>
          </tbody>
        </GlobalStyles.DataTable>
      )}
      {loading ? <CardLoading /> : null}
    </GlobalStyles.DataTableContainer>
    {count > pageSize ? (
      <GlobalStyles.CardFooter style={{ justifyContent: 'flex-end' }}>
        <Pages page={page} setPage={setPage} total={Math.ceil(count / pageSize)} show={5} />
      </GlobalStyles.CardFooter>
    ) : null}
  </DirectivePreviewSection>
)
